import { Fragment, useEffect, useRef, useState } from "react";
import api, { rowKey, fmtMXN, fmtDate, EXPENSE_PAYMENT_METHODS } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Hammer, FileText, DollarSign, ChevronDown, ChevronRight, FileDown, Share2, Upload, Camera, Paperclip, X } from "lucide-react";
import ReceiptViewer from "@/components/ReceiptViewer";
import EvidenceGallery from "@/components/EvidenceGallery";
import { Modal, In, Sel } from "./Projects";

const PIECE_UNITS = [
  { value: "m2", label: "m²" }, { value: "m3", label: "m³" }, { value: "ml", label: "ml" },
  { value: "pza", label: "pza" }, { value: "kg", label: "kg" }, { value: "ton", label: "ton" }, { value: "lote", label: "lote" },
];

const empty = { name: "", rfc: "", phone: "", specialty: "", bank_account: "", notes: "", active: true };

export default function Pieceworkers() {
  const { user } = useAuth();
  const [tab, setTab] = useState("workers");
  const [workers, setWorkers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [openWorker, setOpenWorker] = useState(false);
  const [editingW, setEditingW] = useState(null);
  const [formW, setFormW] = useState(empty);
  const [openContract, setOpenContract] = useState(false);
  const [contractForm, setContractForm] = useState({ pieceworker_id: "", project_id: "", items: [{ _k: rowKey(), description: "", unit: "m2", qty: "", unit_price: "" }], notes: "" });
  const [editingC, setEditingC] = useState(null);
  const [openPay, setOpenPay] = useState(null);
  const [payForm, setPayForm] = useState({ mode: "units", units_paid: "", amount: "", payment_date: "", notes: "" });
  const [payReceiptFile, setPayReceiptFile] = useState(null);
  const [payEvidenceFiles, setPayEvidenceFiles] = useState([]);
  const [paySaving, setPaySaving] = useState(false);
  const [viewerReceipt, setViewerReceipt] = useState(null);
  const [viewerEvidence, setViewerEvidence] = useState(null);
  const payReceiptRef = useRef(null);
  const payEvidenceRef = useRef(null);
  const [expanded, setExpanded] = useState({});
  const [paymentsByContract, setPaymentsByContract] = useState({});
  const [wExpanded, setWExpanded] = useState({});
  const [wSummary, setWSummary] = useState({});
  const [wProjFilter, setWProjFilter] = useState({});
  const canManage = ["admin", "contador", "auxiliar"].includes(user?.role);
  const isResidente = user?.role === "residente";
  const canCreate = canManage || isResidente;
  const canApprove = ["admin", "auxiliar"].includes(user?.role);
  const canPay = ["admin", "contador", "auxiliar", "residente"].includes(user?.role);
  const canDeletePay = ["admin", "contador"].includes(user?.role);

  const load = async () => {
    const [w, c, p] = await Promise.all([api.get("/pieceworkers"), api.get("/piecework-contracts"), api.get("/projects")]);
    setWorkers(w.data); setContracts(c.data); setProjects(p.data);
    setWSummary({});
  };
  const toggleWorker = async (wid) => {
    const next = { ...wExpanded, [wid]: !wExpanded[wid] };
    setWExpanded(next);
    if (next[wid] && !wSummary[wid]) refreshSummary(wid);
  };
  const refreshSummary = async (wid) => {
    const { data } = await api.get(`/pieceworkers/${wid}/summary`);
    setWSummary((prev) => ({ ...prev, [wid]: data }));
  };
  useEffect(() => { load(); }, []);

  // ----- Workers
  const saveWorker = async (e) => {
    e.preventDefault();
    const payload = { ...formW };
    Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
    if (editingW) await api.patch(`/pieceworkers/${editingW}`, payload);
    else await api.post("/pieceworkers", payload);
    setOpenWorker(false); load();
  };
  const removeWorker = async (id) => { if (!window.confirm("¿Eliminar destajista?")) return; await api.delete(`/pieceworkers/${id}`); load(); };

  // ----- Contracts
  const saveContract = async (e) => {
    e.preventDefault();
    const items = contractForm.items
      .filter((it) => it.description.trim() && Number(it.qty) > 0)
      .map((it) => ({ description: it.description.trim(), unit: it.unit, qty: Number(it.qty), unit_price: Number(it.unit_price) || 0 }));
    if (items.length === 0) { alert("Captura al menos un concepto con cantidad y precio"); return; }
    if (editingC) {
      await api.patch(`/piecework-contracts/${editingC}`, { items, notes: contractForm.notes || undefined });
    } else {
      await api.post("/piecework-contracts", {
        pieceworker_id: contractForm.pieceworker_id, project_id: contractForm.project_id,
        items, notes: contractForm.notes || undefined,
      });
    }
    setOpenContract(false); setEditingC(null); load();
  };
  const setItem = (i, k, v) => setContractForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const addItem = () => setContractForm((f) => ({ ...f, items: [...f.items, { _k: rowKey(), description: "", unit: "m2", qty: "", unit_price: "" }] }));
  const removeItem = (i) => setContractForm((f) => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, idx) => idx !== i) : f.items }));
  const contractTotal = contractForm.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const startNewContract = () => { setEditingC(null); setContractForm({ pieceworker_id: workers[0]?.id || "", project_id: projects[0]?.id || "", items: [{ _k: rowKey(), description: "", unit: "m2", qty: "", unit_price: "" }], notes: "" }); setOpenContract(true); };
  const startEditContract = (c) => {
    const items = (c.items && c.items.length ? c.items : [{ description: c.description, unit: c.unit, qty: c.total_units, unit_price: c.unit_price }])
      .map((it) => ({ _k: rowKey(), description: it.description, unit: it.unit, qty: it.qty, unit_price: it.unit_price }));
    setEditingC(c.id); setContractForm({ pieceworker_id: c.pieceworker_id, project_id: c.project_id, items, notes: c.notes || "" }); setOpenContract(true);
  };
  const removeContract = async (id) => { if (!window.confirm("¿Eliminar contrato?")) return; try { await api.delete(`/piecework-contracts/${id}`); load(); } catch (e) { alert(e.response?.data?.detail || "Error"); } };
  const approveContract = async (c) => {
    if (!window.confirm(`¿Aprobar el contrato de ${c.pieceworker_name}? Quedará ACTIVO y se podrán registrar pagos.`)) return;
    try { await api.post(`/piecework-contracts/${c.id}/approve`); load(); } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };
  const rejectContract = async (c) => {
    const reason = window.prompt(`Motivo del rechazo (opcional) — contrato de ${c.pieceworker_name}:`);
    if (reason === null) return;
    try { await api.post(`/piecework-contracts/${c.id}/reject`, { reason: reason || undefined }); load(); } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  // ----- Payments
  const openPayModal = (c) => {
    setOpenPay(c);
    setPayForm({ mode: (c.items || []).length > 1 ? "amount" : "units", units_paid: "", amount: "", payment_date: new Date().toISOString().slice(0, 10), payment_method: "efectivo", notes: "" });
    setPayReceiptFile(null);
    setPayEvidenceFiles([]);
  };
  const savePayment = async (e) => {
    e.preventDefault();
    setPaySaving(true);
    try {
      let receiptId = null;
      if (payReceiptFile) {
        const fd = new FormData();
        fd.append("file", payReceiptFile);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        receiptId = data.id;
      }
      let evidenceIds = [];
      for (const f of payEvidenceFiles) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        evidenceIds = [...evidenceIds, data.id];
      }
      const payload = { contract_id: openPay.id, payment_date: payForm.payment_date, payment_method: payForm.payment_method, receipt_path: receiptId || undefined, evidence_photos: evidenceIds.length ? evidenceIds : undefined, notes: payForm.notes || undefined };
      if (payForm.mode === "amount") payload.amount = Math.round(Number(payForm.amount) * 100) / 100;
      else payload.units_paid = Number(payForm.units_paid);
      await api.post("/piecework-payments", payload);
      setOpenPay(null);
      setPayReceiptFile(null);
      setPayEvidenceFiles([]);
      load();
      loadPayments(openPay.id);
    } catch (err) { alert(err.response?.data?.detail || "Error"); }
    setPaySaving(false);
  };
  const removePayment = async (p) => {
    if (!window.confirm("¿Eliminar este pago? Se revertirá el gasto de obra asociado.")) return;
    try { await api.delete(`/piecework-payments/${p.id}`); load(); loadPayments(p.contract_id); } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };
  const loadPayments = async (cid) => {
    const { data } = await api.get(`/piecework-payments?contract_id=${cid}`);
    setPaymentsByContract((prev) => ({ ...prev, [cid]: data }));
  };
  const pmLabel = (v) => EXPENSE_PAYMENT_METHODS.find((m) => m.value === v)?.label || "—";
  const downloadReceipt = async (p) => {
    try {
      const res = await api.get(`/piecework-payments/${p.id}/receipt/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "recibo_destajo.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("No se pudo generar el recibo"); }
  };
  const shareReceipt = async (p) => {
    try {
      const { data } = await api.post(`/piecework-payments/${p.id}/receipt/share`);
      const url = `${window.location.origin}/firma/recibo-destajo/${data.token}`;
      try { await navigator.clipboard.writeText(url); } catch (e) { /* noop */ }
      window.open(`https://wa.me/?text=${encodeURIComponent(`Recibo de pago de destajo — firma de recibido: ${url}`)}`, "_blank");
    } catch (e) { alert(e.response?.data?.detail || "Error al compartir"); }
  };
  const toggle = async (cid) => {
    const next = { ...expanded, [cid]: !expanded[cid] };
    setExpanded(next);
    if (next[cid] && !paymentsByContract[cid]) loadPayments(cid);
  };

  const projectLabel = (id) => { const p = projects.find((x) => x.id === id); return p ? `${p.code} · ${p.name}` : "—"; };

  return (
    <div data-testid="pieceworkers-page">
      <PageHeader subtitle="Destajistas y contratos por unidad" title="Destajistas"
        actions={canCreate && (
          tab === "workers"
            ? <button data-testid="new-pieceworker-btn" onClick={() => { setEditingW(null); setFormW(empty); setOpenWorker(true); }} className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest"><Plus className="w-4 h-4" /> Nuevo destajista</button>
            : <button data-testid="new-contract-btn" onClick={startNewContract} disabled={!workers.length || !projects.length} className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest disabled:opacity-50"><Plus className="w-4 h-4" /> Nuevo contrato</button>
        )} />
      <div className="px-4 lg:px-8 pt-4">
        <div className="flex gap-2 mb-4">
          <TabBtn active={tab === "workers"} onClick={() => setTab("workers")} testId="tab-workers"><Hammer className="w-3.5 h-3.5" /> Directorio</TabBtn>
          <TabBtn active={tab === "contracts"} onClick={() => setTab("contracts")} testId="tab-contracts"><FileText className="w-3.5 h-3.5" /> Contratos ({contracts.length})</TabBtn>
        </div>
      </div>
      <div className="px-4 lg:px-8 pb-6">
        {tab === "workers" ? (
          <div className="panel overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]" data-testid="workers-table">
              <thead className="panel-header"><tr className="text-left"><Th>Nombre</Th><Th>Especialidad</Th><Th>Teléfono</Th><Th>RFC</Th><Th right>Total entregado</Th><Th right>Acciones</Th></tr></thead>
              <tbody>
                {workers.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-500">Sin destajistas aún.</td></tr>}
                {workers.map((w) => (
                  <Fragment key={w.id}>
                  <tr className="row-hover cursor-pointer" style={{ borderBottom: wExpanded[w.id] ? "none" : "1px solid var(--border)" }} onClick={() => toggleWorker(w.id)} data-testid={`pw-row-${w.id}`}>
                    <Td>
                      <span className="flex items-center gap-1.5">
                        {wExpanded[w.id] ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                        <span className="font-semibold text-slate-100">{w.name}</span>
                      </span>
                    </Td>
                    <Td className="text-xs text-slate-300">{w.specialty || "—"}</Td>
                    <Td className="font-mono text-xs text-slate-300">{w.phone || "—"}</Td>
                    <Td className="font-mono text-xs text-slate-300">{w.rfc || "—"}</Td>
                    <Td right>
                      <span data-testid={`pw-total-${w.id}`} className="font-mono font-bold" style={{ color: (w.total_paid || 0) > 0 ? "var(--green)" : "var(--text-dim)" }}>{fmtMXN(w.total_paid || 0)}</span>
                      {(w.payments_count || 0) > 0 && <span className="text-[10px] text-slate-500 ml-1">({w.payments_count})</span>}
                    </Td>
                    <Td right>
                      <span onClick={(e) => e.stopPropagation()}>
                        {canManage && <button data-testid={`edit-pw-${w.id}`} onClick={() => { setEditingW(w.id); setFormW({ ...empty, ...w }); setOpenWorker(true); }} className="text-xs font-semibold hover:underline mr-3" style={{ color: "var(--cyan)" }}>EDITAR</button>}
                        {user?.role === "admin" && <button data-testid={`delete-pw-${w.id}`} onClick={() => removeWorker(w.id)} className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>}
                      </span>
                    </Td>
                  </tr>
                  {wExpanded[w.id] && (
                    <tr style={{ borderBottom: "1px solid var(--border)" }}>
                      <td colSpan={6} className="px-4 pb-4" data-testid={`pw-detail-${w.id}`}>
                        {!wSummary[w.id]
                          ? <div className="text-xs text-slate-500 py-2">Cargando…</div>
                          : <WorkerDetail s={wSummary[w.id]} wid={w.id} filter={wProjFilter[w.id] || ""} setFilter={(v) => setWProjFilter((prev) => ({ ...prev, [w.id]: v }))} refresh={() => refreshSummary(w.id)} canManage={canManage} isAdmin={["admin", "contador"].includes(user?.role)} />}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="space-y-3">
            {contracts.length === 0 && <div className="panel p-12 text-center text-slate-500">Sin contratos aún.</div>}
            {contracts.map((c) => {
              const isMulti = (c.items || []).length > 1;
              const totalAmount = c.total_amount ?? c.unit_price * c.total_units;
              const paidAmount = c.paid_amount ?? c.unit_price * c.paid_units;
              const paidPct = totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;
              const balanceAmount = Math.max(0, totalAmount - paidAmount);
              return (
                <div key={c.id} data-testid={`contract-${c.id}`} className="panel p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <button onClick={() => toggle(c.id)} className="flex items-center gap-2 text-left">
                        {expanded[c.id] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        <div>
                          <div className="text-xs label-mono" style={{ color: "var(--cyan)" }}>{projectLabel(c.project_id)}</div>
                          <div className="font-semibold text-slate-100">{c.pieceworker_name}</div>
                          <div className="text-sm text-slate-300">{c.description}</div>
                        </div>
                      </button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span data-testid={`contract-status-${c.id}`} className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm" style={{ background: c.status === "completado" ? "rgba(22,163,74,0.18)" : c.status === "pendiente" ? "rgba(245,158,11,0.18)" : ["cancelado", "rechazado"].includes(c.status) ? "rgba(220,38,38,0.18)" : "rgba(34,211,238,0.18)", color: c.status === "completado" ? "var(--green)" : c.status === "pendiente" ? "#f59e0b" : ["cancelado", "rechazado"].includes(c.status) ? "var(--red)" : "var(--cyan)" }}>{c.status === "pendiente" ? "Pendiente de aprobación" : c.status}</span>
                      {canApprove && c.status === "pendiente" && (
                        <>
                          <button data-testid={`approve-contract-${c.id}`} onClick={() => approveContract(c)} className="px-2.5 py-1 text-[10px] uppercase tracking-widest font-semibold rounded-sm" style={{ background: "rgba(22,163,74,0.18)", border: "1px solid var(--green)", color: "var(--green)" }}>✓ Aceptar</button>
                          <button data-testid={`reject-contract-${c.id}`} onClick={() => rejectContract(c)} className="px-2.5 py-1 text-[10px] uppercase tracking-widest font-semibold rounded-sm" style={{ background: "rgba(220,38,38,0.12)", border: "1px solid var(--red)", color: "var(--red)" }}>✕ Rechazar</button>
                        </>
                      )}
                      {(canManage || (isResidente && c.created_by === user?.id && c.status === "pendiente")) && ["activo", "pendiente"].includes(c.status) && <button data-testid={`edit-contract-${c.id}`} onClick={() => startEditContract(c)} className="text-xs font-semibold hover:underline" style={{ color: "var(--cyan)" }}>EDITAR</button>}
                      {canPay && c.status === "activo" && balanceAmount > 0.01 && <button data-testid={`pay-contract-${c.id}`} onClick={() => openPayModal(c)} className="btn-primary px-2.5 py-1 text-[10px] uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1"><DollarSign className="w-3 h-3" /> Registrar pago</button>}
                      {(user?.role === "admin" || (isResidente && c.created_by === user?.id && ["pendiente", "rechazado"].includes(c.status))) && <button data-testid={`delete-contract-${c.id}`} onClick={() => removeContract(c.id)} className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3">
                    {isMulti ? <Kpi label="Conceptos" value={`${c.items.length}`} /> : <Kpi label="Unidades" value={`${c.total_units} ${c.unit}`} />}
                    {isMulti ? <Kpi label="Detalle" value="ver conceptos ▾" /> : <Kpi label="Precio unitario" value={fmtMXN(c.unit_price)} />}
                    <Kpi label="Total contrato" value={fmtMXN(totalAmount)} />
                    <Kpi label="Pagado" value={`${fmtMXN(paidAmount)} (${paidPct.toFixed(0)}%)`} />
                    <Kpi label="Saldo por pagar" value={fmtMXN(balanceAmount)} accent={balanceAmount > 0.01 ? "#f59e0b" : "var(--green)"} />
                  </div>
                  {(c.created_by_name || c.approved_by_name || c.rejection_reason || c.rejected_by_name) && (
                    <div className="mt-2 text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-0.5" data-testid={`contract-meta-${c.id}`}>
                      {c.created_by_name && <span>Creado por: <span className="text-slate-300">{c.created_by_name}</span></span>}
                      {c.approved_by_name && <span>Aprobado por: <span style={{ color: "var(--green)" }}>{c.approved_by_name}</span></span>}
                      {c.rejected_by_name && <span>Rechazado por: <span style={{ color: "var(--red)" }}>{c.rejected_by_name}</span></span>}
                      {c.rejection_reason && <span>Motivo: <span className="text-slate-300">{c.rejection_reason}</span></span>}
                    </div>
                  )}
                  {expanded[c.id] && (
                    <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--border)" }}>
                      {isMulti && (
                        <div className="mb-3 space-y-1" data-testid={`contract-items-${c.id}`}>
                          <div className="label-mono" style={{ color: "var(--cyan)" }}>// CONCEPTOS ({c.items.length})</div>
                          {c.items.map((it, i) => (
                            <div key={`${c.id}-${i}-${it.description}`} className="flex items-center gap-2 text-xs px-2 py-1 rounded-sm" style={{ background: "var(--bg-2)" }}>
                              <span className="text-slate-300 flex-1 truncate">{it.description}</span>
                              <span className="font-mono text-slate-500">{it.qty} {it.unit} × {fmtMXN(it.unit_price)}</span>
                              <span className="font-mono text-slate-100 w-24 text-right">{fmtMXN(it.qty * it.unit_price)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <div className="label-mono" style={{ color: "var(--magenta)" }}>// HISTORIAL DE PAGOS ({(paymentsByContract[c.id] || []).length})</div>
                        {canPay && c.status === "activo" && balanceAmount > 0.01 && (
                          <button data-testid={`add-payment-${c.id}`} onClick={() => openPayModal(c)} className="btn-ghost px-2.5 py-1 text-[10px] uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1" style={{ border: "1px solid var(--green)", color: "var(--green)" }}>
                            <Plus className="w-3 h-3" /> Agregar pago
                          </button>
                        )}
                      </div>
                      {(paymentsByContract[c.id] || []).length === 0 ? (
                        <div className="text-xs text-slate-500 italic">Sin pagos registrados.</div>
                      ) : (
                        <table className="w-full text-xs" data-testid={`payments-table-${c.id}`}>
                          <thead><tr className="text-left text-slate-500"><th className="py-1">Fecha de pago</th><th>Tipo</th><th>Forma de pago</th><th>Unidades</th><th className="text-right">Monto</th><th>Notas</th><th className="text-right">Recibo</th></tr></thead>
                          <tbody>{(paymentsByContract[c.id] || []).map((p) => (
                            <tr key={p.id} style={{ borderTop: "1px solid var(--border)" }}>
                              <td className="py-1.5 font-mono">{fmtDate(p.payment_date || p.created_at)}</td>
                              <td>
                                {p.payment_type
                                  ? <span data-testid={`payment-type-${p.id}`} className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-sm" style={p.payment_type === "liquidacion" ? { background: "rgba(22,163,74,0.18)", color: "var(--green)" } : { background: "rgba(245,158,11,0.15)", color: "#f59e0b" }}>{p.payment_type === "liquidacion" ? "Liquidación" : "Anticipo"}</span>
                                  : <span className="text-slate-500">—</span>}
                              </td>
                              <td className="text-slate-300" data-testid={`payment-method-${p.id}`}>{pmLabel(p.payment_method)}</td>
                              <td className="font-mono">{p.units_paid} {c.unit}</td>
                              <td className="text-right font-mono font-semibold" style={{ color: "var(--green)" }}>{fmtMXN(p.amount)}</td>
                              <td className="text-slate-400">{p.notes || "—"}</td>
                              <td className="text-right whitespace-nowrap">
                                {p.receipt_path && <button data-testid={`view-pay-receipt-${p.id}`} onClick={() => setViewerReceipt({ fileId: p.receipt_path, title: `Comprobante · ${p.pieceworker_name || ""}` })} title="Ver comprobante de pago" className="p-1 rounded-sm hover:bg-white/5" style={{ color: "#fbbf24" }}><Paperclip className="w-3.5 h-3.5" /></button>}
                                {(p.evidence_photos || []).length > 0 && <button data-testid={`view-pay-evidence-${p.id}`} onClick={() => setViewerEvidence({ photos: p.evidence_photos, title: `Evidencias · ${p.pieceworker_name || ""}` })} title={`Ver ${p.evidence_photos.length} evidencia(s)`} className="p-1 rounded-sm hover:bg-white/5" style={{ color: "#fbbf24" }}><Camera className="w-3.5 h-3.5" /></button>}
                                <button data-testid={`receipt-pdf-${p.id}`} onClick={() => downloadReceipt(p)} title="Descargar recibo PDF" className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--cyan)" }}><FileDown className="w-3.5 h-3.5" /></button>
                                {canPay && <button data-testid={`receipt-share-${p.id}`} onClick={() => shareReceipt(p)} title="Compartir para firma (WhatsApp)" className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--green)" }}><Share2 className="w-3.5 h-3.5" /></button>}
                                {p.signed_at && <span data-testid={`receipt-signed-${p.id}`} title={`Firmado por ${p.signed_name}`} className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-sm ml-1" style={{ background: "rgba(22,163,74,0.18)", color: "var(--green)" }}>✓ Firmado</span>}
                                {canDeletePay && <button data-testid={`delete-payment-${p.id}`} onClick={() => removePayment(p)} className="p-1 rounded-sm hover:bg-white/5 ml-1" style={{ color: "var(--red)" }} title="Eliminar pago"><Trash2 className="w-3.5 h-3.5" /></button>}
                              </td>
                            </tr>
                          ))}</tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {openWorker && (
        <Modal onClose={() => setOpenWorker(false)} title={editingW ? "Editar destajista" : "Nuevo destajista"}>
          <form onSubmit={saveWorker} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <In label="Nombre" testId="pw-name" value={formW.name} onChange={(v) => setFormW({ ...formW, name: v })} />
              <In label="Especialidad" testId="pw-specialty" value={formW.specialty} onChange={(v) => setFormW({ ...formW, specialty: v })} required={false} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <In label="Teléfono" testId="pw-phone" value={formW.phone} onChange={(v) => setFormW({ ...formW, phone: v })} required={false} />
              <In label="RFC" testId="pw-rfc" value={formW.rfc} onChange={(v) => setFormW({ ...formW, rfc: v })} required={false} />
            </div>
            <In label="Cuenta bancaria" testId="pw-bank" value={formW.bank_account} onChange={(v) => setFormW({ ...formW, bank_account: v })} required={false} />
            <div>
              <label className="label-mono block mb-1.5">Notas</label>
              <textarea value={formW.notes} onChange={(e) => setFormW({ ...formW, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpenWorker(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" data-testid="save-pw-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Guardar</button>
            </div>
          </form>
        </Modal>
      )}

      {openContract && (
        <Modal onClose={() => setOpenContract(false)} title={editingC ? "Editar contrato" : "Nuevo contrato de destajo"}>
          <form onSubmit={saveContract} className="space-y-4">
            {!editingC && isResidente && (
              <div className="text-xs px-3 py-2.5 rounded-sm" style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.4)", color: "#f59e0b" }} data-testid="contract-pending-notice">
                El contrato quedará <b>PENDIENTE DE APROBACIÓN</b>. Un administrador o auxiliar deberá aceptarlo para que quede activo.
              </div>
            )}
            {!editingC && (
              <div className="grid grid-cols-2 gap-3">
                <Sel label="Destajista" testId="contract-pw" value={contractForm.pieceworker_id} onChange={(v) => setContractForm({ ...contractForm, pieceworker_id: v })} options={workers.map((w) => ({ value: w.id, label: w.name }))} />
                <Sel label="Proyecto" testId="contract-project" value={contractForm.project_id} onChange={(v) => setContractForm({ ...contractForm, project_id: v })} options={projects.map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }))} />
              </div>
            )}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="label-mono" style={{ color: "var(--magenta)" }}>// CONCEPTOS DEL CONTRATO</span>
                <button type="button" data-testid="contract-add-item" onClick={addItem} className="btn-ghost px-2.5 py-1 text-[10px] uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1" style={{ color: "var(--cyan)" }}>
                  <Plus className="w-3 h-3" /> Agregar concepto
                </button>
              </div>
              {contractForm.items.map((it, i) => (
                <div key={it._k || i} className="grid gap-2 items-end" style={{ gridTemplateColumns: "1fr 84px 84px 100px 24px" }}>
                  <div>
                    {i === 0 && <label className="label-mono block mb-1">Descripción</label>}
                    <input required value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} className="w-full input-cyber px-2.5 py-2 text-sm" data-testid={`contract-item-desc-${i}`} placeholder="Ej. Muro de block" />
                  </div>
                  <div>
                    {i === 0 && <label className="label-mono block mb-1">Unidad</label>}
                    <select value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} className="w-full input-cyber px-2 py-2 text-sm" data-testid={`contract-item-unit-${i}`}>
                      {PIECE_UNITS.map((u) => <option key={u.value} value={u.value} style={{ background: "#0a0f1d" }}>{u.label}</option>)}
                    </select>
                  </div>
                  <div>
                    {i === 0 && <label className="label-mono block mb-1">Cantidad</label>}
                    <input required type="number" step="any" min="0" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="w-full input-cyber px-2 py-2 text-sm text-right" data-testid={`contract-item-qty-${i}`} />
                  </div>
                  <div>
                    {i === 0 && <label className="label-mono block mb-1">P. Unit. MXN</label>}
                    <input required type="number" step="0.01" min="0" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} className="w-full input-cyber px-2 py-2 text-sm text-right" data-testid={`contract-item-price-${i}`} />
                  </div>
                  <button type="button" onClick={() => removeItem(i)} disabled={contractForm.items.length === 1} className="pb-2 text-slate-500 hover:text-red-400 disabled:opacity-30" data-testid={`contract-item-remove-${i}`}><X className="w-4 h-4" /></button>
                </div>
              ))}
              <div className="text-[10px] text-slate-500 text-right font-mono">
                {contractForm.items.map((it, i) => {
                  const sub = (Number(it.qty) || 0) * (Number(it.unit_price) || 0);
                  return sub > 0 ? <span key={it._k || i} className="ml-3">#{i + 1}: {fmtMXN(sub)}</span> : null;
                })}
              </div>
            </div>
            <div className="px-3 py-2 rounded-sm text-sm" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }} data-testid="contract-total-preview">
              Total del contrato ({contractForm.items.filter((i) => i.description.trim()).length} conceptos): <span className="font-bold font-mono" style={{ color: "var(--cyan)" }}>{fmtMXN(contractTotal)}</span>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Notas</label>
              <textarea value={contractForm.notes} onChange={(e) => setContractForm({ ...contractForm, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpenContract(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" data-testid="save-contract-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Guardar</button>
            </div>
          </form>
        </Modal>
      )}

      {openPay && (() => {
        const payMulti = (openPay.items || []).length > 1;
        const unitPrice = Number(openPay.unit_price) || 0;
        const totalC = openPay.total_amount ?? openPay.total_units * unitPrice;
        const paidC = openPay.paid_amount ?? openPay.paid_units * unitPrice;
        const remUnits = Math.max(0, openPay.total_units - openPay.paid_units);
        const remAmount = Math.max(0, totalC - paidC);
        const payUnits = payForm.mode === "amount"
          ? (unitPrice > 0 && !payMulti ? (Number(payForm.amount) || 0) / unitPrice : 0)
          : (Number(payForm.units_paid) || 0);
        const payAmount = payForm.mode === "amount" ? (Number(payForm.amount) || 0) : payUnits * unitPrice;
        const exceeds = payMulti ? payAmount > remAmount + 0.01 : payUnits > remUnits + 0.001;
        const afterBalance = Math.max(0, remAmount - payAmount);
        return (
        <Modal onClose={() => setOpenPay(null)} title={`Registrar pago · ${openPay.pieceworker_name}`}>
          <form onSubmit={savePayment} className="space-y-4">
            <div className="px-3 py-2 rounded-sm text-xs text-slate-400 flex flex-wrap gap-x-4 gap-y-1" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }}>
              <span>Contrato: <span className="font-mono font-bold text-slate-100">{fmtMXN(totalC)}</span>{payMulti ? ` (${openPay.items.length} conceptos)` : ""}</span>
              <span>Pagado: <span className="font-mono font-bold" style={{ color: "var(--green)" }}>{fmtMXN(paidC)}</span></span>
              <span>Saldo: <span className="font-mono font-bold" style={{ color: "#f59e0b" }}>{fmtMXN(remAmount)}</span>{payMulti ? "" : ` (${remUnits.toFixed(2)} ${openPay.unit})`}</span>
            </div>
            {!payMulti && (
            <div>
              <label className="label-mono block mb-1.5">Forma de captura</label>
              <div className="flex gap-2">
                <button type="button" data-testid="pay-mode-units" onClick={() => setPayForm({ ...payForm, mode: "units" })}
                  className="flex-1 px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm"
                  style={payForm.mode === "units" ? { background: "rgba(34,211,238,0.15)", border: "1px solid var(--cyan)", color: "var(--cyan)" } : { border: "1px solid var(--border)", color: "var(--text-dim)" }}>
                  Por unidades ({openPay.unit})
                </button>
                <button type="button" data-testid="pay-mode-amount" onClick={() => setPayForm({ ...payForm, mode: "amount" })}
                  className="flex-1 px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm"
                  style={payForm.mode === "amount" ? { background: "rgba(34,211,238,0.15)", border: "1px solid var(--cyan)", color: "var(--cyan)" } : { border: "1px solid var(--border)", color: "var(--text-dim)" }}>
                  Por monto ($)
                </button>
              </div>
            </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {payForm.mode === "amount"
                ? <In label="Monto a pagar (MXN)" testId="pay-amount" type="number" value={payForm.amount} onChange={(v) => setPayForm({ ...payForm, amount: v })} />
                : <In label={`Unidades a pagar (${openPay.unit})`} testId="pay-units" type="number" value={payForm.units_paid} onChange={(v) => setPayForm({ ...payForm, units_paid: v })} />}
              <In label="Fecha de pago" testId="pay-date" type="date" value={payForm.payment_date} onChange={(v) => setPayForm({ ...payForm, payment_date: v })} />
            </div>
            <Sel label="Forma de pago" testId="pay-method" value={payForm.payment_method} onChange={(v) => setPayForm({ ...payForm, payment_method: v })} options={EXPENSE_PAYMENT_METHODS} />
            {payForm.payment_method === "efectivo" && (
              <div className="text-xs px-3 py-2 rounded-sm" data-testid="pay-cash-hint" style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.4)", color: "#f59e0b" }}>
                Pago en EFECTIVO: el gasto contará en tu comprobación de efectivo (caja) y descontará tu saldo por comprobar.
              </div>
            )}
            <div>
              <label className="label-mono block mb-1.5">Comprobante de pago (imagen o PDF)</label>
              <div className="border border-dashed rounded-sm p-4 text-center cursor-pointer transition-colors hover:bg-white/5"
                style={{ borderColor: "var(--border-2)" }}
                onClick={() => payReceiptRef.current?.click()}>
                <input ref={payReceiptRef} type="file" accept="image/*,application/pdf" onChange={(e) => setPayReceiptFile(e.target.files[0])} className="hidden" data-testid="pay-receipt-input" />
                <Upload className="w-5 h-5 mx-auto mb-1" style={{ color: "var(--cyan)" }} />
                <div className="text-xs text-slate-300">{payReceiptFile ? payReceiptFile.name : "Click para subir comprobante (transferencia, recibo, etc.)"}</div>
              </div>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Evidencias del trabajo (fotos)</label>
              <div className="border border-dashed rounded-sm p-4 text-center cursor-pointer transition-colors hover:bg-white/5"
                style={{ borderColor: "rgba(251,191,36,0.35)" }}
                onClick={() => payEvidenceRef.current?.click()}>
                <input ref={payEvidenceRef} type="file" accept="image/*" multiple onChange={(ev) => { const files = Array.from(ev.target.files || []); setPayEvidenceFiles((prev) => [...prev, ...files]); ev.target.value = ""; }} className="hidden" data-testid="pay-evidence-input" />
                <Camera className="w-5 h-5 mx-auto mb-1" style={{ color: "#fbbf24" }} />
                <div className="text-xs text-slate-300">{payEvidenceFiles.length > 0 ? `${payEvidenceFiles.length} foto(s) por subir` : "Fotos del trabajo terminado o avance"}</div>
              </div>
              {payEvidenceFiles.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {payEvidenceFiles.map((f, i) => (
                    <span key={`${f.name}-${f.size}-${f.lastModified}`} className="px-2 py-0.5 rounded-sm text-[10px] font-mono flex items-center gap-1.5" style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)", color: "#fbbf24" }}>
                      {f.name.slice(0, 20)}
                      <button type="button" data-testid={`pay-remove-evidence-${i}`} onClick={() => setPayEvidenceFiles(payEvidenceFiles.filter((_, j) => j !== i))} className="font-bold">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="px-3 py-2 rounded-sm text-sm space-y-0.5" style={{ background: "var(--bg-2)", border: exceeds ? "1px solid var(--red)" : "1px solid var(--border)" }}>
              <div>
                {payForm.mode === "amount"
                  ? (payMulti
                    ? <>Monto a pagar: <span className="font-bold font-mono" style={{ color: "var(--green)" }}>{fmtMXN(payAmount)}</span></>
                    : <>Equivale a: <span className="font-bold font-mono" style={{ color: "var(--cyan)" }}>{payUnits.toFixed(2)} {openPay.unit}</span></>)
                  : <>Monto a pagar: <span className="font-bold font-mono" style={{ color: "var(--green)" }}>{fmtMXN(payAmount)}</span></>}
              </div>
              <div className="text-xs" data-testid="pay-after-balance">
                {exceeds
                  ? <span style={{ color: "var(--red)" }}>⚠ Excede el saldo del contrato ({fmtMXN(remAmount)})</span>
                  : <span className="text-slate-400">Saldo después de este pago: <span className="font-mono font-semibold" style={{ color: afterBalance > 0.01 ? "#f59e0b" : "var(--green)" }}>{fmtMXN(afterBalance)}</span>{afterBalance <= 0.01 && " · el contrato quedará COMPLETADO"}</span>}
              </div>
            </div>
            <div className="text-xs text-slate-500">Se generará un gasto automático (categoría: Destajos) en el proyecto con la fecha y forma de pago indicadas, ya aprobado. Podrás descargar o compartir el recibo de pago desde el historial.</div>
            <div>
              <label className="label-mono block mb-1.5">Notas</label>
              <textarea value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpenPay(null)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" data-testid="save-payment-btn" disabled={exceeds || payAmount <= 0 || paySaving} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">{paySaving ? "Guardando…" : "Confirmar pago"}</button>
            </div>
          </form>
        </Modal>
        );
      })()}

      {viewerReceipt && (
        <ReceiptViewer fileId={viewerReceipt.fileId} title={viewerReceipt.title} onClose={() => setViewerReceipt(null)} />
      )}
      {viewerEvidence && (
        <EvidenceGallery photos={viewerEvidence.photos} title={viewerEvidence.title} onClose={() => setViewerEvidence(null)} />
      )}
    </div>
  );
}

const WorkerDetail = ({ s, wid, filter, setFilter, refresh, canManage, isAdmin }) => {
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const pmLabelW = (v) => EXPENSE_PAYMENT_METHODS.find((m) => m.value === v)?.label || "—";
  const payments = filter ? s.payments.filter((p) => p.project_id === filter) : s.payments;
  const total = payments.reduce((a, p) => a + p.amount, 0);
  const projName = (id) => { const p = s.projects.find((x) => x.id === id); return p ? `${p.code} · ${p.name}` : "—"; };
  const downloadStatement = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/pieceworkers/${wid}/statement/pdf`, { params: filter ? { project_id: filter } : {}, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "estado_de_cuenta_destajista.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("No se pudo generar el PDF"); }
    setDownloading(false);
  };
  const shareForSign = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/pieceworkers/${wid}/statements/share`, null, { params: filter ? { project_id: filter } : {} });
      const url = `${window.location.origin}/firma/destajo/${data.token}`;
      try { await navigator.clipboard.writeText(url); } catch (e) { /* noop */ }
      window.open(`https://wa.me/?text=${encodeURIComponent(`Estado de cuenta de destajos — firma de recibido: ${url}`)}`, "_blank");
      refresh();
    } catch (e) { alert(e.response?.data?.detail || "No se pudo compartir"); }
    setSharing(false);
  };
  const copyStatementLink = async (st) => {
    const url = `${window.location.origin}/firma/destajo/${st.share_token}`;
    try { await navigator.clipboard.writeText(url); alert("Enlace copiado"); } catch (e) { window.prompt("Copia el enlace:", url); }
  };
  const downloadStatementPdf = async (st) => {
    const res = await api.get(`/pieceworkers/statements/${st.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "estado_de_cuenta_destajista.pdf"; a.click();
    URL.revokeObjectURL(url);
  };
  const removeStatement = async (st) => {
    if (!window.confirm("¿Eliminar este estado de cuenta compartido?")) return;
    try { await api.delete(`/pieceworkers/statements/${st.id}`); refresh(); } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };
  return (
    <div className="mt-1 pt-3" style={{ borderTop: "1px dashed var(--border)" }}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="label-mono" style={{ color: "var(--magenta)" }}>// RECURSOS ENTREGADOS</div>
        <select data-testid={`pw-project-filter-${wid}`} value={filter} onChange={(e) => setFilter(e.target.value)} className="input-cyber px-2 py-1 text-xs">
          <option value="">Todos los proyectos ({s.projects.length})</option>
          {s.projects.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
        </select>
        <div className="text-xs text-slate-400">
          {filter ? "Total en proyecto:" : "Total general:"}{" "}
          <span data-testid={`pw-filtered-total-${wid}`} className="font-mono font-bold" style={{ color: "var(--green)" }}>{fmtMXN(total)}</span>
          <span className="text-slate-500 ml-1">({payments.length} {payments.length === 1 ? "pago" : "pagos"})</span>
        </div>
        <button data-testid={`pw-statement-btn-${wid}`} onClick={downloadStatement} disabled={downloading}
          className="btn-ghost px-2.5 py-1 text-[10px] uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5 disabled:opacity-50"
          style={{ border: "1px solid var(--cyan)", color: "var(--cyan)" }}>
          <FileDown className="w-3.5 h-3.5" /> {downloading ? "Generando…" : "Estado de cuenta PDF"}
        </button>
        {canManage && (
          <button data-testid={`pw-share-statement-btn-${wid}`} onClick={shareForSign} disabled={sharing}
            className="btn-ghost px-2.5 py-1 text-[10px] uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5 disabled:opacity-50"
            style={{ border: "1px solid var(--green)", color: "var(--green)" }}>
            <Share2 className="w-3.5 h-3.5" /> {sharing ? "Compartiendo…" : "Compartir para firmar"}
          </button>
        )}
      </div>
      {(s.statements || []).length > 0 && (
        <div className="mb-3" data-testid={`pw-statements-${wid}`}>
          <div className="label-mono mb-1.5" style={{ color: "var(--cyan)" }}>// ESTADOS DE CUENTA COMPARTIDOS</div>
          <div className="space-y-1">
            {s.statements.map((st) => (
              <div key={st.id} className="flex flex-wrap items-center gap-2 px-2.5 py-1.5 rounded-sm text-xs" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }}>
                <span className="font-mono text-slate-300">{fmtDate(st.created_at)}</span>
                <span className="text-slate-500">{st.project ? `${st.project.code} · ${st.project.name}` : "Todos los proyectos"}</span>
                <span className="font-mono font-semibold" style={{ color: "var(--green)" }}>{fmtMXN(st.total_paid)}</span>
                {st.signed_at
                  ? <span className="text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-sm" style={{ background: "rgba(22,163,74,0.18)", color: "var(--green)" }}>✓ Firmado · {st.signed_name}</span>
                  : <span className="text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-sm" style={{ background: "rgba(245,158,11,0.15)", color: "var(--amber, #f59e0b)" }}>Sin firma</span>}
                <span className="flex items-center gap-2 ml-auto">
                  {!st.signed_at && <button data-testid={`copy-statement-link-${st.id}`} onClick={() => copyStatementLink(st)} className="text-[10px] uppercase tracking-widest font-semibold hover:underline" style={{ color: "var(--cyan)" }}>Copiar enlace</button>}
                  <button data-testid={`statement-pdf-${st.id}`} onClick={() => downloadStatementPdf(st)} className="text-[10px] uppercase tracking-widest font-semibold hover:underline" style={{ color: "var(--cyan)" }}>PDF</button>
                  {isAdmin && !st.signed_at && <button data-testid={`delete-statement-${st.id}`} onClick={() => removeStatement(st)} className="p-0.5 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-3.5 h-3.5" /></button>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {!filter && s.projects.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid={`pw-project-chips-${wid}`}>
          {s.projects.map((p) => (
            <button key={p.id} onClick={() => setFilter(p.id)} className="px-2 py-1 rounded-sm text-[11px] hover:bg-white/5" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }}>
              <span className="label-mono mr-1.5" style={{ color: "var(--cyan)" }}>{p.code}</span>
              <span className="font-mono font-semibold" style={{ color: "var(--green)" }}>{fmtMXN((s.by_project[p.id] || {}).total || 0)}</span>
            </button>
          ))}
        </div>
      )}
      {payments.length === 0 ? (
        <div className="text-xs text-slate-500 italic">Sin pagos registrados{filter ? " en este proyecto" : ""}.</div>
      ) : (
        <table className="w-full text-xs" data-testid={`pw-payments-${wid}`}>
          <thead><tr className="text-left text-slate-500"><th className="py-1">Fecha</th><th>Proyecto</th><th>Trabajo</th><th>Unidades</th><th>Forma de pago</th><th className="text-right">Monto</th><th>Notas</th></tr></thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td className="py-1.5 font-mono">{fmtDate(p.payment_date || p.created_at)}</td>
                <td><span className="label-mono" style={{ color: "var(--cyan)" }}>{projName(p.project_id)}</span></td>
                <td className="text-slate-300">{p.contract_description || "—"}</td>
                <td className="font-mono">{p.units_paid} {p.unit || ""}</td>
                <td className="text-slate-300">{pmLabelW(p.payment_method)}</td>
                <td className="text-right font-mono font-semibold" style={{ color: "var(--green)" }}>{fmtMXN(p.amount)}</td>
                <td className="text-slate-400">{p.notes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const Th = ({ children, right }) => <th className={`label-mono px-4 py-3 ${right ? "text-right" : ""}`}>{children}</th>;
const Td = ({ children, right, className = "" }) => <td className={`px-4 py-3 align-top ${right ? "text-right" : ""} ${className}`}>{children}</td>;
const Kpi = ({ label, value, accent }) => (
  <div className="px-2.5 py-1.5 rounded-sm" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }}>
    <div className="label-mono">{label}</div>
    <div className="font-mono font-bold text-sm" style={{ color: accent || "var(--text, #f1f5f9)" }}>{value}</div>
  </div>
);
const TabBtn = ({ active, onClick, children, testId }) => (
  <button data-testid={testId} onClick={onClick} className="px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5"
    style={{ background: active ? "rgba(34,211,238,0.12)" : "transparent", color: active ? "var(--cyan)" : "var(--text-dim)", border: `1px solid ${active ? "var(--cyan)" : "var(--border)"}` }}>
    {children}
  </button>
);
